import type { TaskCardDemoRow } from '@/app/[locale]/tasks/components/TasksTaskCards/tasksTaskCards.constants'
import type { AvailableTaskCard } from '@/features/tasks/api/watchTaskApi'
import { mapAvailableTaskCardToTaskRow } from '@/features/tasks/mappers/mapAvailableTaskCardToTaskRow'
import { getTaskCardDetailPath } from '@/features/tasks/taskCardCta'
import { isValidHttpUrl } from '@/utils/url'

/** GET /api/v2/task-banners item: texts already in the request language. */
export type PublicTaskBanner = {
  id: string
  title: string
  subtitle?: string
  cta_text?: string
  cta_url?: string
  cta_note?: string
  sponsor_name?: string
  sponsor_logo_url?: string
  sponsor_url?: string
  desktop_image_url: string
  mobile_image_url: string
  task?: AvailableTaskCard
  /** Tasks (platforms) of the campaign a guest can take. */
  campaign_task_count?: number
}

export type ListTaskBannersResponse = {
  banners: PublicTaskBanner[]
}

export type ResolvedTaskBanner = {
  id: string
  title: string
  sponsorName?: string
  sponsorLogoUrl?: string
  sponsorUrl?: string
  description?: string
  /** Stats row for a task banner without its own subtitle. */
  task?: TaskCardDemoRow
  /** Undefined means the default "View task" label. */
  ctaText?: string
  href?: string
  external: boolean
  note?: string
  /** Shown as "N tasks in this campaign" when the editor left the note empty. */
  campaignTaskCount?: number
  desktopImageUrl: string
  mobileImageUrl: string
}

function ctaTarget(
  url: string | undefined
): { href: string; external: boolean } | undefined {
  const value = url?.trim()
  if (!value) {
    return undefined
  }
  if (value.startsWith('/') && !value.startsWith('//')) {
    return { href: value, external: false }
  }
  return isValidHttpUrl(value) ? { href: value, external: true } : undefined
}

/** Fills what the editor left empty from the linked task. */
export function resolveTaskBanner(
  banner: PublicTaskBanner
): ResolvedTaskBanner {
  const task = banner.task
    ? mapAvailableTaskCardToTaskRow(banner.task)
    : undefined
  const taskHref = task ? getTaskCardDetailPath(task) : null
  const cta =
    ctaTarget(banner.cta_url) ??
    (taskHref ? { href: taskHref, external: false } : undefined)
  const description = banner.subtitle?.trim() || undefined

  return {
    id: banner.id,
    title: banner.title,
    sponsorName: banner.sponsor_name || task?.apiBrandName,
    sponsorLogoUrl: banner.sponsor_logo_url || banner.task?.brand_logo_url,
    sponsorUrl: isValidHttpUrl(banner.sponsor_url)
      ? banner.sponsor_url
      : undefined,
    description,
    task: description ? undefined : task,
    ctaText: banner.cta_text?.trim() || undefined,
    href: cta?.href,
    external: cta?.external ?? false,
    note: banner.cta_note?.trim() || undefined,
    // One task is not worth a note.
    campaignTaskCount:
      !banner.cta_note?.trim() &&
      banner.task &&
      (banner.campaign_task_count ?? 0) > 1
        ? banner.campaign_task_count
        : undefined,
    desktopImageUrl: banner.desktop_image_url,
    mobileImageUrl: banner.mobile_image_url
  }
}
